import React, { useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Table from '../../components/tables/Table';
import Button from '../../components/buttons/Button';
import { CustomCard } from '../../components/cards/CustomCard';
import SearchBar from '../../components/searchBar/SearchBar';
import DropDown from '../../components/inputFields/DropDown';
import InputField from '../../components/inputFields/InputField';
import TextField from '../../components/inputFields/TextField';
import ShowModal from '../../utils/ShowModal';
import Card from '../../components/cards/Card';
import Tab from '../../components/tabs/Tab';
import DetailedViewModal from '../../components/DetailedViewModal';
import { CheckCircle, Wrench, XCircle, CheckSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const DamageReplacements = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showReportDamageModal, setShowReportDamageModal] = useState(false);
    const [showReplacementModal, setShowReplacementModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showStatusReportModal, setShowStatusReportModal] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [activeTab, setActiveTab] = useState('damage');

    // Status tracking data
    const [statusTracking, setStatusTracking] = useState({
        total: 120,
        working: 80,
        damaged: 20,
        repairable: 20,
        lastUpdated: '2024-01-23',
        updatedBy: 'Ashutosh'
    });

    // Damage reports data with enhanced status tracking
    const [damageReports, setDamageReports] = useState([
        {
            id: 1,
            type: 'incoming',
            itemName: 'Solar Panel 100W',
            sku: 'SOL-001',
            quantity: 5,
            supplier: 'GreenEnergy Corp',
            reportedBy: 'John Doe',
            reportedDate: '2024-01-23',
            damageType: 'physical',
            severity: 'major',
            description: 'Cracked glass on multiple panels during shipping',
            status: 'damaged',
            estimatedLoss: '₹25,000',
            images: ['panel1.jpg', 'panel2.jpg'],
            location: 'Receiving Dock A',
            repairStatus: 'pending',
            repairDate: null,
            repairedBy: null,
            repairNotes: ''
        },
        {
            id: 2,
            type: 'outgoing',
            itemName: 'Controller Module',
            sku: 'CTL-001',
            quantity: 2,
            customer: 'ABC Electronics',
            reportedBy: 'Jane Smith',
            reportedDate: '2024-01-22',
            damageType: 'defective',
            severity: 'minor',
            description: 'Faulty display showing incorrect readings',
            status: 'repairable',
            estimatedLoss: '₹2,400',
            images: ['controller1.jpg'],
            location: 'Quality Check',
            repairStatus: 'in_progress',
            repairDate: null,
            repairedBy: 'Ashutosh',
            repairNotes: 'Display module replacement in progress'
        },
        {
            id: 3,
            type: 'incoming',
            itemName: 'Battery 12V',
            sku: 'BAT-001',
            quantity: 3,
            supplier: 'PowerCell Industries',
            reportedBy: 'Mike Johnson',
            reportedDate: '2024-01-21',
            damageType: 'water_damage',
            severity: 'critical',
            description: 'Water damage due to packaging failure',
            status: 'repaired',
            estimatedLoss: '₹8,400',
            images: ['battery1.jpg', 'battery2.jpg'],
            location: 'Warehouse B',
            repairStatus: 'completed',
            repairDate: '2024-01-22',
            repairedBy: 'Ashutosh',
            repairNotes: 'Battery cells replaced, tested and working'
        },
        {
            id: 4,
            type: 'outgoing',
            itemName: 'LED Light Kit',
            sku: 'LED-001',
            quantity: 10,
            customer: 'Solar Solutions Ltd',
            reportedBy: 'Sarah Wilson',
            reportedDate: '2024-01-20',
            damageType: 'physical',
            severity: 'major',
            description: 'Broken LED units due to poor packaging',
            status: 'damaged',
            estimatedLoss: '₹5,500',
            images: ['led1.jpg'],
            location: 'Shipping Area',
            repairStatus: 'not_repairable',
            repairDate: null,
            repairedBy: null,
            repairNotes: 'Beyond repair - disposal recommended'
        }
    ]);

    // Replacement requests data
    const [replacementRequests, setReplacementRequests] = useState([
        {
            id: 1,
            damageReportId: 1,
            itemName: 'Solar Panel 100W',
            sku: 'SOL-001',
            requestedQuantity: 5,
            approvedQuantity: 5,
            requestedBy: 'John Doe',
            requestDate: '2024-01-23',
            approvedBy: 'Admin',
            approvalDate: '2024-01-23',
            status: 'approved',
            priority: 'high',
            expectedDelivery: '2024-01-30',
            supplier: 'GreenEnergy Corp',
            estimatedCost: '₹25,000',
            reason: 'Customer order pending'
        },
        {
            id: 2,
            damageReportId: 2,
            itemName: 'Controller Module',
            sku: 'CTL-001',
            requestedQuantity: 2,
            approvedQuantity: 0,
            requestedBy: 'Jane Smith',
            requestDate: '2024-01-22',
            status: 'pending',
            priority: 'medium',
            expectedDelivery: '',
            supplier: 'Smart Controls Inc',
            estimatedCost: '₹2,400',
            reason: 'Quality assurance replacement'
        },
        {
            id: 3,
            damageReportId: 3,
            itemName: 'Battery 12V',
            sku: 'BAT-001',
            requestedQuantity: 3,
            approvedQuantity: 3,
            requestedBy: 'Mike Johnson',
            requestDate: '2024-01-21',
            approvedBy: 'Admin',
            approvalDate: '2024-01-21',
            status: 'delivered',
            priority: 'high',
            expectedDelivery: '2024-01-28',
            actualDelivery: '2024-01-27',
            supplier: 'PowerCell Industries',
            estimatedCost: '₹8,400',
            reason: 'Immediate customer requirement'
        }
    ]);

    const [newDamageReport, setNewDamageReport] = useState({
        type: 'incoming',
        itemName: '',
        sku: '',
        quantity: '',
        supplier: '',
        customer: '',
        damageType: '',
        severity: '',
        description: '',
        location: ''
    });

    const [newReplacementRequest, setNewReplacementRequest] = useState({
        damageReportId: '',
        requestedQuantity: '',
        priority: 'medium',
        reason: '',
        expectedDelivery: ''
    });

    // Updated stats cards data with status tracking
    const statsCardsData = [
        {
            title: "Total Items",
            count: statusTracking.total,
            icon: CheckSquare,
            bgGradient: "from-blue-50 to-blue-100",
            textColor: "text-blue-900"
        },
        {
            title: "Working Items",
            count: statusTracking.working,
            icon: CheckCircle,
            bgGradient: "from-green-50 to-green-100",
            textColor: "text-green-900"
        },
        {
            title: "Damaged Items",
            count: statusTracking.damaged,
            icon: XCircle,
            bgGradient: "from-red-50 to-red-100",
            textColor: "text-red-900"
        },
        {
            title: "Repairable Items",
            count: statusTracking.repairable,
            icon: Wrench,
            bgGradient: "from-yellow-50 to-yellow-100",
            textColor: "text-yellow-900"
        }
    ];

    const damageTypes = [
        { value: 'physical', label: 'Physical Damage' },
        { value: 'defective', label: 'Manufacturing Defect' },
        { value: 'water_damage', label: 'Water Damage' },
        { value: 'electrical', label: 'Electrical Fault' },
        { value: 'packaging', label: 'Packaging Damage' }
    ];

    const severityOptions = [
        { value: 'minor', label: 'Minor' },
        { value: 'major', label: 'Major' },
        { value: 'critical', label: 'Critical' }
    ];

    const statusOptions = [
        { value: 'all', label: 'All Status' },
        { value: 'reported', label: 'Reported' },
        { value: 'investigating', label: 'Investigating' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'damaged', label: 'Damaged' },
        { value: 'repairable', label: 'Repairable' },
        { value: 'repaired', label: 'Repaired' }
    ];

    const typeOptions = [
        { value: 'all', label: 'All Types' },
        { value: 'incoming', label: 'Incoming' },
        { value: 'outgoing', label: 'Outgoing' }
    ];

    const priorityOptions = [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' }
    ];

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (status) {
            case 'reported':
                return `${baseClasses} bg-blue-100 text-blue-800`;
            case 'investigating':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'approved':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'rejected':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'pending':
                return `${baseClasses} bg-orange-100 text-orange-800`;
            case 'delivered':
                return `${baseClasses} bg-purple-100 text-purple-800`;
            case 'damaged':
                return `${baseClasses} bg-red-100 text-red-800`;
            case 'repairable':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'repaired':
                return `${baseClasses} bg-green-100 text-green-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const getSeverityBadge = (severity) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (severity) {
            case 'minor':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'major':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'critical':
                return `${baseClasses} bg-red-100 text-red-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const getPriorityBadge = (priority) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (priority) {
            case 'low':
                return `${baseClasses} bg-gray-100 text-gray-800`;
            case 'medium':
                return `${baseClasses} bg-blue-100 text-blue-800`;
            case 'high':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'urgent':
                return `${baseClasses} bg-red-100 text-red-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const getRepairStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
        switch (status) {
            case 'pending':
                return `${baseClasses} bg-gray-100 text-gray-800`;
            case 'in_progress':
                return `${baseClasses} bg-blue-100 text-blue-800`;
            case 'completed':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'not_repairable':
                return `${baseClasses} bg-red-100 text-red-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    // Function to update status tracking
    const updateStatusTracking = (newStatus, oldStatus) => {
        setStatusTracking(prev => {
            const newTracking = { ...prev };

            // Decrease old status count
            if (oldStatus === 'damaged') newTracking.damaged--;
            else if (oldStatus === 'repairable') newTracking.repairable--;
            else if (oldStatus === 'repaired') newTracking.working++;

            // Increase new status count
            if (newStatus === 'damaged') newTracking.damaged++;
            else if (newStatus === 'repairable') newTracking.repairable++;
            else if (newStatus === 'repaired') {
                newTracking.working++;
                newTracking.repairable--;
            }

            newTracking.lastUpdated = new Date().toISOString().split('T')[0];
            newTracking.updatedBy = 'Ashutosh';

            return newTracking;
        });
    };

    // Function to update report status
    const updateReportStatus = (reportId, newStatus, repairData = {}) => {
        setDamageReports(prev => prev.map(report => {
            if (report.id === reportId) {
                const oldStatus = report.status;
                const updatedReport = {
                    ...report,
                    status: newStatus,
                    ...repairData
                };

                // Update status tracking
                updateStatusTracking(newStatus, oldStatus);

                return updatedReport;
            }
            return report;
        }));
    };

    // Function to mark as repaired
    const markAsRepaired = (reportId, repairNotes) => {
        updateReportStatus(reportId, 'repaired', {
            repairStatus: 'completed',
            repairDate: new Date().toISOString().split('T')[0],
            repairedBy: 'Ashutosh',
            repairNotes: repairNotes
        });
    };

    // Function to mark as damaged
    const markAsDamaged = (reportId) => {
        updateReportStatus(reportId, 'damaged', {
            repairStatus: 'not_repairable',
            repairDate: null,
            repairedBy: null,
            repairNotes: 'Marked as permanently damaged'
        });
    };

    // Function to mark as repairable
    const markAsRepairable = (reportId) => {
        updateReportStatus(reportId, 'repairable', {
            repairStatus: 'pending',
            repairDate: null,
            repairedBy: null,
            repairNotes: ''
        });
    };

    const filteredDamageReports = damageReports.filter(report => {
        const matchesSearch = report.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            report.sku.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || report.type === filterType;
        const matchesStatus = filterStatus === 'all' || report.status === filterStatus;

        return matchesSearch && matchesType && matchesStatus;
    });

    const filteredReplacements = replacementRequests.filter(request => {
        const matchesSearch = request.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.sku.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const handleReportDamage = () => {
        const id = Math.max(...damageReports.map(report => report.id)) + 1;
        const reportToAdd = {
            ...newDamageReport,
            id,
            reportedBy: 'Current User',
            reportedDate: new Date().toISOString().split('T')[0],
            status: 'reported',
            estimatedLoss: '₹0',
            images: [],
            repairStatus: 'pending', // Default repair status
            repairDate: null,
            repairedBy: null,
            repairNotes: ''
        };

        setDamageReports([...damageReports, reportToAdd]);
        setNewDamageReport({
            type: 'incoming', itemName: '', sku: '', quantity: '', supplier: '', customer: '',
            damageType: '', severity: '', description: '', location: ''
        });
        setShowReportDamageModal(false);
    };

    const handleRequestReplacement = () => {
        const id = Math.max(...replacementRequests.map(req => req.id)) + 1;
        const selectedReport = damageReports.find(report => report.id === parseInt(newReplacementRequest.damageReportId));

        const requestToAdd = {
            ...newReplacementRequest,
            id,
            itemName: selectedReport?.itemName || '',
            sku: selectedReport?.sku || '',
            requestedBy: 'Current User',
            requestDate: new Date().toISOString().split('T')[0],
            status: 'pending',
            approvedQuantity: 0,
            supplier: selectedReport?.supplier || '',
            estimatedCost: selectedReport?.estimatedLoss || '₹0'
        };

        setReplacementRequests([...replacementRequests, requestToAdd]);
        setNewReplacementRequest({
            damageReportId: '', requestedQuantity: '', priority: 'medium', reason: '', expectedDelivery: ''
        });
        setShowReplacementModal(false);
    };

    // Function to open details modal
    const openDetailsModal = (report) => {
        setSelectedReport(report);
        setShowDetailsModal(true);
    };

    // Function to handle status updates from detailed view
    const handleStatusUpdate = (reportId, newStatus) => {
        updateReportStatus(reportId, newStatus);
    };

    // Tab configuration
    const tabsList = [
        {
            id: 'damage',
            name: 'Damage Reports',
            count: filteredDamageReports.length
        },
        {
            id: 'replacements',
            name: 'Replacement Requests',
            count: filteredReplacements.length
        }
    ];

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Inventory' },
                { name: 'Damage & Replacements' },
            ]} />

            <div className="flex justify-between items-center mb-6">
                <StyledHeadingText text={"Damage & Replacement Management"} size={"text-3xl"} />
                <div className="flex space-x-3">
                    <Link
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
                        to="/inventory/damage-replacements/report-damage"
                    >
                        Report Damage
                    </Link>
                    <Link
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg"
                        to="/inventory/damage-replacements/request-replacement"
                    >
                        Request Replacement
                    </Link>
                    <Link
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
                        to="/inventory/damage-replacements/status-report"
                    >
                        Status Report
                    </Link>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                {statsCardsData.map((card, index) => (
                    <Card
                        key={index}
                        title={card.title}
                        count={card.count}
                        icon={card.icon}
                        bgGradient={card.bgGradient}
                        textColor={card.textColor}
                    />
                ))}
            </div>

            {/* Tab Navigation */}
            <div className="mb-6">
                <Tab
                    tabsList={tabsList}
                    activeTab={activeTab}
                    onClick={setActiveTab}
                />
            </div>

            {/* Filters Section */}
            <CustomCard title="Filters & Search">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <SearchBar
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            placeholder="Search by item name or SKU..."
                        />
                    </div>
                    {activeTab === 'damage' && (
                        <>
                            <DropDown
                                options={typeOptions}
                                value={filterType}
                                onChange={setFilterType}
                                placeholder="Filter by Type"
                            />
                            <DropDown
                                options={statusOptions}
                                value={filterStatus}
                                onChange={setFilterStatus}
                                placeholder="Filter by Status"
                            />
                        </>
                    )}
                </div>
            </CustomCard>

            {/* Damage Reports Tab */}
            {activeTab === 'damage' && (
                <CustomCard title={`Damage Reports (${filteredDamageReports.length})`}>
                    <Table
                        headers={[
                            { label: "Item Details", key: "item" },
                            { label: "Type & Quantity", key: "type" },
                            { label: "Damage Info", key: "damage" },
                            { label: "Status", key: "status" },
                            { label: "Repair Status", key: "repair" },
                            { label: "Actions", key: "actions" }
                        ]}
                        rows={filteredDamageReports.map((report) => ({
                            item: (
                                <div>
                                    <p className="font-semibold">{report.itemName}</p>
                                    <p className="text-sm text-gray-600">{report.sku}</p>
                                    <p className="text-xs text-gray-500">{report.location}</p>
                                </div>
                            ),
                            type: (
                                <div>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${report.type === 'incoming' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                        {report.type === 'incoming' ? 'Incoming' : 'Outgoing'}
                                    </span>
                                    <p className="text-sm font-medium mt-1">{report.quantity} units</p>
                                </div>
                            ),
                            damage: (
                                <div>
                                    <p className="text-sm font-medium">{report.damageType.replace('_', ' ')}</p>
                                    <span className={getSeverityBadge(report.severity)}>
                                        {report.severity.charAt(0).toUpperCase() + report.severity.slice(1)}
                                    </span>
                                </div>
                            ),
                            status: (
                                <div>
                                    <span className={getStatusBadge(report.status)}>
                                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                                    </span>
                                    <p className="text-sm text-red-600 font-medium">{report.estimatedLoss}</p>
                                </div>
                            ),
                            repair: (
                                <div>
                                    <span className={getRepairStatusBadge(report.repairStatus)}>
                                        {report.repairStatus.replace('_', ' ')}
                                    </span>
                                    {report.repairedBy && (
                                        <p className="text-xs text-gray-500 mt-1">By: {report.repairedBy}</p>
                                    )}
                                </div>
                            ),
                            actions: (
                                <div className="flex flex-col space-y-2">
                                    <Button
                                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 text-xs rounded"
                                        onClick={() => openDetailsModal(report)}
                                    >
                                        View Details
                                    </Button>
                                    {report.status === 'reported' && (
                                        <div className="flex space-x-1">
                                            <Button
                                                className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 text-xs rounded"
                                                onClick={() => markAsRepairable(report.id)}
                                            >
                                                Repairable
                                            </Button>
                                            <Button
                                                className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 text-xs rounded"
                                                onClick={() => markAsDamaged(report.id)}
                                            >
                                                Damaged
                                            </Button>
                                        </div>
                                    )}
                                    {report.status === 'repairable' && (
                                        <Button
                                            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 text-xs rounded"
                                            onClick={() => markAsRepaired(report.id, 'Repaired by Ashutosh')}
                                        >
                                            Mark Repaired
                                        </Button>
                                    )}
                                </div>
                            )
                        }))}
                    />
                </CustomCard>
            )}

            {/* Replacement Requests Tab */}
            {activeTab === 'replacements' && (
                <CustomCard title={`Replacement Requests (${filteredReplacements.length})`}>
                    <Table
                        headers={[
                            { label: "Item Details", key: "item" },
                            { label: "Quantities", key: "quantities" },
                            { label: "Request Info", key: "request" },
                            { label: "Status & Priority", key: "status" },
                            { label: "Delivery", key: "delivery" },
                            { label: "Actions", key: "actions" }
                        ]}
                        rows={filteredReplacements.map((request) => ({
                            item: (
                                <div>
                                    <p className="font-semibold">{request.itemName}</p>
                                    <p className="text-sm text-gray-600">{request.sku}</p>
                                    <p className="text-xs text-gray-500">{request.supplier}</p>
                                </div>
                            ),
                            quantities: (
                                <div>
                                    <p className="text-sm">Requested: <span className="font-semibold">{request.requestedQuantity}</span></p>
                                    <p className="text-sm">Approved: <span className="font-semibold">{request.approvedQuantity}</span></p>
                                    <p className="text-sm text-green-600">{request.estimatedCost}</p>
                                </div>
                            ),
                            request: (
                                <div>
                                    <p className="font-medium">{request.requestedBy}</p>
                                    <p className="text-sm text-gray-600">{request.requestDate}</p>
                                    <p className="text-xs text-gray-500">{request.reason}</p>
                                </div>
                            ),
                            status: (
                                <div>
                                    <span className={getStatusBadge(request.status)}>
                                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                    </span>
                                    <div className="mt-1">
                                        <span className={getPriorityBadge(request.priority)}>
                                            {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                                        </span>
                                    </div>
                                </div>
                            ),
                            delivery: (
                                <div>
                                    {request.expectedDelivery && (
                                        <p className="text-sm">Expected: {request.expectedDelivery}</p>
                                    )}
                                    {request.actualDelivery && (
                                        <p className="text-sm text-green-600">Delivered: {request.actualDelivery}</p>
                                    )}
                                    {request.approvedBy && (
                                        <p className="text-xs text-gray-500">By: {request.approvedBy}</p>
                                    )}
                                </div>
                            ),
                            actions: (
                                <div className="flex flex-col space-y-2">
                                    {request.status === 'pending' && (
                                        <>
                                            <Button className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 text-xs rounded">
                                                Approve
                                            </Button>
                                            <Button className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 text-xs rounded">
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                    <Button className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 text-xs rounded">
                                        View Details
                                    </Button>
                                </div>
                            )
                        }))}
                    />
                </CustomCard>
            )}

            {/* Report Damage Modal */}
            <ShowModal
                show={showReportDamageModal}
                onClose={() => setShowReportDamageModal(false)}
                title="Report Damage"
                size="lg"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <DropDown
                        label="Damage Type"
                        options={typeOptions.filter(opt => opt.value !== 'all')}
                        value={newDamageReport.type}
                        onChange={(value) => setNewDamageReport({ ...newDamageReport, type: value })}
                        placeholder="Select damage type"
                    />
                    <InputField
                        label="Item Name"
                        value={newDamageReport.itemName}
                        onChange={(e) => setNewDamageReport({ ...newDamageReport, itemName: e.target.value })}
                        placeholder="Enter item name"
                    />
                    <InputField
                        label="SKU"
                        value={newDamageReport.sku}
                        onChange={(e) => setNewDamageReport({ ...newDamageReport, sku: e.target.value })}
                        placeholder="Enter SKU"
                    />
                    <InputField
                        label="Quantity"
                        type="number"
                        value={newDamageReport.quantity}
                        onChange={(e) => setNewDamageReport({ ...newDamageReport, quantity: e.target.value })}
                        placeholder="Enter quantity"
                    />
                    {newDamageReport.type === 'incoming' ? (
                        <InputField
                            label="Supplier"
                            value={newDamageReport.supplier}
                            onChange={(e) => setNewDamageReport({ ...newDamageReport, supplier: e.target.value })}
                            placeholder="Enter supplier name"
                        />
                    ) : (
                        <InputField
                            label="Customer"
                            value={newDamageReport.customer}
                            onChange={(e) => setNewDamageReport({ ...newDamageReport, customer: e.target.value })}
                            placeholder="Enter customer name"
                        />
                    )}
                    <DropDown
                        label="Damage Type"
                        options={damageTypes}
                        value={newDamageReport.damageType}
                        onChange={(value) => setNewDamageReport({ ...newDamageReport, damageType: value })}
                        placeholder="Select damage type"
                    />
                    <DropDown
                        label="Severity"
                        options={severityOptions}
                        value={newDamageReport.severity}
                        onChange={(value) => setNewDamageReport({ ...newDamageReport, severity: value })}
                        placeholder="Select severity"
                    />
                    <InputField
                        label="Location"
                        value={newDamageReport.location}
                        onChange={(e) => setNewDamageReport({ ...newDamageReport, location: e.target.value })}
                        placeholder="Enter location"
                    />
                </div>

                <div className="mb-4">
                    <TextField
                        label="Description"
                        value={newDamageReport.description}
                        onChange={(e) => setNewDamageReport({ ...newDamageReport, description: e.target.value })}
                        placeholder="Describe the damage in detail..."
                        rows={4}
                    />
                </div>

                <div className="flex justify-end space-x-3">
                    <Button
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                        onClick={() => setShowReportDamageModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                        onClick={handleReportDamage}
                    >
                        Report Damage
                    </Button>
                </div>
            </ShowModal>

            {/* Request Replacement Modal */}
            <ShowModal
                show={showReplacementModal}
                onClose={() => setShowReplacementModal(false)}
                title="Request Replacement"
                size="lg"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <DropDown
                        label="Damage Report"
                        options={damageReports.filter(report => report.status === 'approved').map(report => ({
                            value: report.id,
                            label: `${report.itemName} - ${report.quantity} units`
                        }))}
                        value={newReplacementRequest.damageReportId}
                        onChange={(value) => setNewReplacementRequest({ ...newReplacementRequest, damageReportId: value })}
                        placeholder="Select damage report"
                    />
                    <InputField
                        label="Requested Quantity"
                        type="number"
                        value={newReplacementRequest.requestedQuantity}
                        onChange={(e) => setNewReplacementRequest({ ...newReplacementRequest, requestedQuantity: e.target.value })}
                        placeholder="Enter quantity"
                    />
                    <DropDown
                        label="Priority"
                        options={priorityOptions}
                        value={newReplacementRequest.priority}
                        onChange={(value) => setNewReplacementRequest({ ...newReplacementRequest, priority: value })}
                        placeholder="Select priority"
                    />
                    <InputField
                        label="Expected Delivery"
                        type="date"
                        value={newReplacementRequest.expectedDelivery}
                        onChange={(e) => setNewReplacementRequest({ ...newReplacementRequest, expectedDelivery: e.target.value })}
                    />
                </div>

                <div className="mb-4">
                    <TextField
                        label="Reason for Replacement"
                        value={newReplacementRequest.reason}
                        onChange={(e) => setNewReplacementRequest({ ...newReplacementRequest, reason: e.target.value })}
                        placeholder="Explain why replacement is needed..."
                        rows={3}
                    />
                </div>

                <div className="flex justify-end space-x-3">
                    <Button
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                        onClick={() => setShowReplacementModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded"
                        onClick={handleRequestReplacement}
                    >
                        Request Replacement
                    </Button>
                </div>
            </ShowModal>

            {/* Status Report Modal */}
            <ShowModal
                show={showStatusReportModal}
                onClose={() => setShowStatusReportModal(false)}
                title="Status Report"
                size="lg"
            >
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-semibold text-lg mb-3">Current Status</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                                    <span className="font-medium">Working Items:</span>
                                    <span className="text-2xl font-bold text-green-600">{statusTracking.working}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                                    <span className="font-medium">Damaged Items:</span>
                                    <span className="text-2xl font-bold text-red-600">{statusTracking.damaged}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded">
                                    <span className="font-medium">Repairable Items:</span>
                                    <span className="text-2xl font-bold text-yellow-600">{statusTracking.repairable}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                                    <span className="font-medium">Total Items:</span>
                                    <span className="text-2xl font-bold text-blue-600">{statusTracking.total}</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-semibold text-lg mb-3">Recent Updates</h4>
                            <div className="space-y-2">
                                <p><span className="font-medium">Last Updated:</span> {statusTracking.lastUpdated}</p>
                                <p><span className="font-medium">Updated By:</span> {statusTracking.updatedBy}</p>
                                <p><span className="font-medium">Working Rate:</span> {((statusTracking.working / statusTracking.total) * 100).toFixed(1)}%</p>
                                <p><span className="font-medium">Damage Rate:</span> {((statusTracking.damaged / statusTracking.total) * 100).toFixed(1)}%</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-lg mb-3">Recent Status Changes</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                            {damageReports
                                .filter(report => report.repairDate || report.status === 'damaged' || report.status === 'repairable')
                                .slice(0, 5)
                                .map((report, index) => (
                                    <div key={index} className="p-2 bg-gray-50 rounded text-sm">
                                        <p><span className="font-medium">{report.itemName}</span> - {report.status}</p>
                                        {report.repairDate && (
                                            <p className="text-gray-600">Repaired on {report.repairDate} by {report.repairedBy}</p>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                            onClick={() => setShowStatusReportModal(false)}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </ShowModal>

            {/* Detailed View Modal */}
            <DetailedViewModal
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                report={selectedReport}
                onStatusUpdate={handleStatusUpdate}
                getStatusBadge={getStatusBadge}
                getSeverityBadge={getSeverityBadge}
                getRepairStatusBadge={getRepairStatusBadge}
            />
        </>
    );
};

export default DamageReplacements; 